import type { Level } from './constants';
import type { ProductType } from './productDefinition';
import { ConfigurationTypes, Products } from './productDefinition';
import { capitalizeFirstLetter, isExtraConfigurationInProduct } from './utils';

export enum Section {
  Fabric = 'fabric',
  Jacket = 'jacket',
  Trousers = 'trousers',
  Waistcoat = 'waistcoat',
  Coat = 'coat',
  TuxedoTrouser = 'tuxedoTrouser',
  TuxedoShirt = 'tuxedoShirt',
  Shirt = 'shirt',
  TuxedoJacket = 'tuxedoJacket',
  TuxedoWaistcoat = 'tuxedoWaistcoat',
  Default = 'default',
  Summary = 'summary',
  Style = 'style',
  ExtraTrouser = 'extraTrouser',
  ExtraTuxedoTrouser = 'extraTuxedoTrouser',
}

const getNavigationTexts = (configurationTypeId: ConfigurationTypes) => {
  switch (configurationTypeId) {
    case ConfigurationTypes.TuxedoJacket:
      return 'Jacket';
    case ConfigurationTypes.TuxedoTrouser:
      return 'Trousers';
    case ConfigurationTypes.TuxedoWaistcoat:
      return 'Waistcoat';
    default:
      return capitalizeFirstLetter(getSectionByConfigurationType(configurationTypeId));
  }
};

export const getProductSections = (product: ProductType) => {
  const sectionList = [{ text: 'Fabric', slug: Section.Fabric }];
  if (Products[product].length === 1) {
    sectionList.push({
      text: 'Style',
      slug: getSectionByConfigurationType(Products[product][0]),
    });
  } else {
    Products[product].forEach(configurationType => {
      if (!isExtraConfigurationInProduct(product, configurationType)) {
        sectionList.push({
          text: getNavigationTexts(configurationType),
          slug: getSectionByConfigurationType(configurationType),
        });
      }
    });
  }
  return sectionList;
};

export const getSectionByConfigurationType = (configurationType: ConfigurationTypes | string) => {
  switch (configurationType) {
    case ConfigurationTypes.Jacket:
      return Section.Jacket;
    case ConfigurationTypes.Trousers:
      return Section.Trousers;
    case ConfigurationTypes.Waistcoat:
      return Section.Waistcoat;
    case ConfigurationTypes.Coat:
      return Section.Coat;
    case ConfigurationTypes.TuxedoTrouser:
      return Section.TuxedoTrouser;
    case ConfigurationTypes.TuxedoShirt:
      return Section.TuxedoShirt;
    case ConfigurationTypes.Shirt:
      return Section.Shirt;
    case ConfigurationTypes.TuxedoJacket:
      return Section.TuxedoJacket;
    case ConfigurationTypes.TuxedoWaistcoat:
      return Section.TuxedoWaistcoat;
    case ConfigurationTypes.ExtraTrouser:
      return Section.ExtraTrouser;
    case ConfigurationTypes.ExtraTuxedoTrouser:
      return Section.ExtraTuxedoTrouser;
    default:
      return Section.Fabric;
  }
};

export const getConfigurationTypeBySection = (section: string) => {
  switch (section) {
    case Section.Jacket:
      return ConfigurationTypes.Jacket;
    case Section.Trousers:
      return ConfigurationTypes.Trousers;
    case Section.Waistcoat:
      return ConfigurationTypes.Waistcoat;
    case Section.Coat:
      return ConfigurationTypes.Coat;
    case Section.TuxedoTrouser:
      return ConfigurationTypes.TuxedoTrouser;
    case Section.TuxedoShirt:
      return ConfigurationTypes.TuxedoShirt;
    case Section.Shirt:
      return ConfigurationTypes.Shirt;
    case Section.TuxedoJacket:
      return ConfigurationTypes.TuxedoJacket;
    case Section.TuxedoWaistcoat:
      return ConfigurationTypes.TuxedoWaistcoat;
    case Section.ExtraTrouser:
      return ConfigurationTypes.ExtraTrouser;
    case Section.ExtraTuxedoTrouser:
      return ConfigurationTypes.ExtraTuxedoTrouser;
    default:
      return ConfigurationTypes.Jacket;
  }
};

const setHistoryState = (section: string | null, level?: string | null) => {
  const queryParams = new URLSearchParams(window.location.search);
  if (section && section.length) {
    queryParams.set('section', section);
  }
  if (level && level.length) {
    queryParams.set('level', level);
  }
  window.history.pushState(null, '', `?${queryParams.toString()}`);
};

export const changeSection = (
  toSection: string,
  toLevel: Level,
  setSection: (section: string) => void,
  setLevel: (level: Level) => void,
) => {
  setSection(toSection);
  setLevel(toLevel);
  setHistoryState(toSection, toLevel);
};

export const changeLevel = (level: Level, setLevel: (level: Level) => void) => {
  setLevel(level);
  setHistoryState(null, level);
};

export const getProductDefaultSectionFromQueryParameter = (section: Section | undefined, product: ProductType) => {
  if (!section) {
    return Section.Fabric;
  }

  if (section === Section.Summary) {
    return Section.Summary;
  }

  const productSections = getProductSections(product);
  const matchingSectionFromUrl = productSections.find(productSection => productSection.slug === section);

  if (!matchingSectionFromUrl) {
    return Section.Fabric;
  }

  return matchingSectionFromUrl.slug;
};

/** Index of the preview slide for a section: the section's garment, else the last one; null without a product. */
export const getPreviewSlideIndex = (
  product: ProductType | undefined,
  section: string | null,
  isExtraProduct: boolean,
) => {
  if (isExtraProduct) {
    return 0;
  }
  if (!product) {
    return null;
  }
  if (section === Section.Fabric) {
    return 0;
  }

  const configurationTypes = Products[product];
  const sectionIndex = configurationTypes.findIndex(
    configurationType => getSectionByConfigurationType(configurationType) === section,
  );

  return sectionIndex >= 0 ? sectionIndex : configurationTypes.length - 1;
};
